import cn from 'classnames';
import { useRouter } from 'next/router';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import Paginator from '@creditflowresearch/uilib/Paginator';
import {
  BodyWrapper,
  dataTableStyles,
  HeaderRow,
  HeaderWrapper,
  TableEmptyRow,
} from '@creditflowresearch/uilib/Table/DataTable';
import { IColumn } from '@creditflowresearch/uilib/Table/DataTable/interfaces/Column';
import type { ITimeZone } from '@dealroadshow/uikit-date-time';
import { DEFAULT_TIME_ZONE } from '@framework/timezone/constants';
import getBwicsInventoryMetaSchema from '@/creditflowresearch/ui/components/bonds/getBwicsInventoryMetaSchema';
import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema';
import { IAssetClass } from '@/creditflowresearch/domain/vo/IAssetClass';

import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import styles from '@/creditflowresearch/ui/components/bonds/common/BondTables/bondsTables.module.scss';

interface IBwicProps {
  isFetching: boolean,
  isItemsPerPageVisible?: boolean,
  data: Record<string, any>,
  totalCount: number,
  sortBy: string,
  sortOrder: string,
  page: number,
  perPage: number,
  getHeaderColumns: (columnsConditions?: Record<string, boolean>, timeZone?: ITimeZone) => IColumn[],
  bwicRow: any,
  tableFooterLabel: React.ReactNode,
  sort: (sortBy: string, sortOrder: string, currentAssetClassId?: IAssetClass['id']) => void,
  paginate: (selected: number, currentAssetClassId?: IAssetClass['id']) => void,
  itemsPerPage: (value: number, currentAssetClassId?: IAssetClass['id']) => void,
  withSchema?: boolean,
  currentAssetClass?: IAssetClass,
  /** Zone the header columns render dates in. Callers outside CreditFlow (e.g. Condor
   * bond import) have no user zone and fall back to DEFAULT_TIME_ZONE. */
  timeZone?: ITimeZone,
}

const Bwic = (props: IBwicProps) => {
  const {
    data,
    totalCount,
    page,
    perPage,
    sortBy,
    sortOrder,
    isFetching,
    withSchema = true,
    isItemsPerPageVisible,
    bwicRow: BwicRow,
    getHeaderColumns,
    currentAssetClass,
    timeZone = DEFAULT_TIME_ZONE,
  } = props;
  const { pathname } = useRouter();

  const handlePageChange = (page: { selected: number }) => {
    props.paginate(page.selected, currentAssetClass?.id);
  };

  const handlePerPageChange = (perPage: { value: number, label: string }) => {
    props.itemsPerPage(perPage.value, currentAssetClass?.id);
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    props.sort(sortBy, sortOrder, currentAssetClass?.id);
  };

  const tableClass = cn(
    dataTableStyles.dataTable,
    dataTableStyles.isHoverable,
    dataTableStyles.nowrapCells,
  );

  const emptyMessageClass = cn(
    dataTableStyles.emptyMessage,
    styles.emptyMessage,
    spacesStyles.ptxl,
    spacesStyles.pbxl,
  );

  const headerColumns = getHeaderColumns(undefined, timeZone);

  return (
    <>
      {
        withSchema && (
          <MetaSchema schema={ getBwicsInventoryMetaSchema(pathname, 'BWICS', currentAssetClass?.abbreviation) } />
        )
      }
      <div className={ cn(dataTableStyles.dataTableContainer, styles.tableContainer) }>
        { isFetching && (
          <Spinner overlay />
        ) }

        <table className={ tableClass } data-test="bwicTable">
          <HeaderWrapper>
            <HeaderRow
              sortBy={ sortBy }
              sortOrder={ sortOrder }
              onSortChange={ handleSortChange }
              columns={ headerColumns }
            />
          </HeaderWrapper>

          <BodyWrapper>
            { data?.length ? data.map((row, index) => (
              <BwicRow
                /* eslint-disable-next-line react/no-array-index-key */
                key={ index }
                rowIndex={ index }
                row={ row }
              />
            )) : (
              <TableEmptyRow
                columnsCount={ headerColumns.length }
                dataTest="bwicTable"
                isFetching={ isFetching }
                className={ emptyMessageClass }
              />
            ) }
          </BodyWrapper>
        </table>

        <Paginator
          dataTest="bondsTableBwic"
          page={ page }
          perPage={ perPage }
          totalCount={ totalCount }
          onPageChange={ handlePageChange }
          onItemsPerPageChange={ handlePerPageChange }
          labelOfText={ props.tableFooterLabel }
          isItemsPerPageVisible={ isItemsPerPageVisible }
        />
      </div>
    </>
  );
};

export default Bwic;
